/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect} from "react"
import {useTranslation} from "react-i18next"
import {Icon} from "components/Icon"
import {BaseButton} from "components/buttons"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {Screen} from "components/ui"
import {kitService} from "services"
import {KlassappTableHOC} from "HOC"
import styles from "./Kits.module.css"
import {useHistory} from "react-router-dom"
import {getFullName, handleError} from "helpers"

const Kits = (props) => {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        isLoading,
        menuActions,
        isHideMenuActions,
        isShowTableHeaderAction,
        isLoadedTableFuncs,
        getCurrentPage,
        getCurrentPageSize
    } = props
    const {t} = useTranslation(["kit", "common"])
    const history = useHistory()

    const getFields = () => {
        return [
            t("kit:kitName"),
            t("kit:totalKitCategories"),
            t("kit:createdAt"),
            t("kit:lastUpdated"),
            t("kit:lastModifiedBy")
        ]
    }

    const getPageTitle = () => {
        return t("kit:title")
    }

    const getColumns = () => {
        return [
            {
                title: t("kit:kitName"),
                field: "name",
                sortable: true,
                style: {minWidth: "200px"}
            },
            {
                title: t("kit:totalKitCategories"),
                field: "totalKitCategories",
                style: {minWidth: "140px"}
            },
            {
                title: t("kit:createdAt"),
                field: "createdAt",
                fieldType: "dateTime",
                sortable: true,
                style: {minWidth: "160px"}
            },
            {
                title: t("kit:lastUpdated"),
                field: "updatedAt",
                fieldType: "dateTime",
                sortable: true,
                style: {minWidth: "160px"}
            },
            {
                title: t("kit:lastModifiedBy"),
                field: "updatedByProfile",
                style: {minWidth: "160px"},
                render: (data) => getFullName(data)
            }
        ]
    }

    const getParams = () => {
        const currentPage = getCurrentPage()
        const currentPageSize = getCurrentPageSize()
        const params: Record<string, unknown> = {
            range: {page: currentPage, pageSize: currentPageSize}
        }
        if (orderField?.field) {
            params.sort = {
                orderBy: orderField.field,
                orderDir: orderField.order ?? "DESC"
            }
        }
        return params
    }

    const getKits = async () => {
        dispatch({isLoading: true})
        try {
            const params = getParams()
            const {data: listData, total: totalCount} = await kitService.getAll(params)
            dispatch({data: listData, total: totalCount || 0})
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClickAdd = () => {
        history.push("/settings/kits/detail")
    }

    const onClickRowItem = (row) => {
        history.push(`/settings/kits/detail/${row.kitId}`)
    }

    const onClickEdit = (editedItem) => {
        onClickRowItem(editedItem)
    }

    const onClickDelete = async (deletedItem) => {
        try {
            dispatch({isLoading: true})
            await kitService.deleteKit(deletedItem.kitId)
            await getKits()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClickDeleteMulti = async () => {
        const checkedItems = data.filter((item) => item.isChecked)
        const kitIds = checkedItems.map((item) => item.kitId)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await Promise.all(kitIds.map((id) => kitService.deleteKit(id)))
            await getKits()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const getMenuActions = () => {
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: onClickEdit
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: onClickDelete
            }
        ]
    }

    const getTableHeaderActions = () => {
        return [
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => dispatch({isModalVisible: true, modalAction: "DELETE"})
            }
        ]
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getKits},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickRowItem", func: onClickRowItem},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete},
            {key: "getTableHeaderActions", func: getTableHeaderActions}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getKits()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order])

    useEffect(() => {
        dispatchFunc([{key: "onClickDeleteMulti", func: onClickDeleteMulti}])
        props.updateTableHeaderActions?.()
    }, [data])

    return (
        <Screen
            htmlTitle={t("kit:title")}
            header={{
                title: t("kit:title")
            }}>
            <div className={styles.createBtnWrap}>
                <BaseButton
                    title={t("common:add").toUpperCase()}
                    onClick={onClickAdd}
                    icon={<Icon className={styles.plusIcon} icon="PLUS" color="var(--whitebase)" />}
                />
            </div>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                menuActions={isHideMenuActions ? [] : menuActions}
                isLoading={isLoading}
                allFields={allFields}
                isShowCheckedColumn
                orderField={orderField}
                keyRender="kitId"
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
        </Screen>
    )
}

export default KlassappTableHOC(Kits)
